import Vue from 'vue';

import apiDocuments from '@src/api/documents';
import apiApp from '@src/api/app';
import ApiDocumentCard from '@src/api/entities/ApiDocumentCard';

import { 
  isNotChar,
  isStandAlone,
  getMatches,
  getMatchesForPhrases,
  markTextWithMatches } from '@src/utils/textSearchHelpers';

import DOC_CONFIG from './propertiesConfig';

export default {
  state: {
    currDocId: '',
    currDocInfo: {},
    currDocContinuity: '',
    currDocAnnotation: '',
    currDocChanges: [],
    currDocClassifiers: [],
    currDocScanContent: [],
    currDocScanPages: [],
    currDocTextContent: [],
    currDocTextHtml: '',
    currDocTextHeadData: {},
    currDocTextBlocks: [],
    currDocHistory: [],
    currDocDiscussions: [],
    currDocRevisions: [],
    currDocAttachments: [],
    loaderState: {
      info: false,
      continuity: false,
      annotation: false,
      changes: false,
      classifiers: false,
      scanContent: false,
      scanPages: false,
      textContent: false,
      textHtml: false,
      textBlock: false,
      history: false,
      discussions: false,
      revisions: false,
      attachments: false,
      search: false
    },
    loadErrors: {
      info: { state: false, message: '' },
      continuity: { state: false, message: '' },
      annotation: { state: false, message: '' },
      changes: { state: false, message: '' },
      classifiers: { state: false, message: '' },
      scanContent: { state: false, message: '' },
      scanPages: { state: false, message: '' },
      textContent: { state: false, message: '' },
      textHtml: { state: false, message: '' },
      textBlock: { state: false, message: '' },
      history: { state: false, message: '' },
      discussions: { state: false, message: '' },
      revisions: { state: false, message: '' },
      attachments: { state: false, message: '' },
      search: { state: false, message: '' }
    },

    viewedDocs: {},

    // -------------------------- SEARCH STATE DATA --------------------------
    // Универсальные данные для поискаа соответствия
    searchMatchesCount: null,
    currentSearchMatch: 0,
    // Данные специфические для поиска по текстовому виду
    markedTextBlock: '',     // Размеченный метками совпадений HTML для текстового вида
    // Данные специфические для поиска по сканам
    foundScanMatches: [],  // Массив совпадений в сканах с координатами областей
  },

  getters: {
    currentDocumentId: state => state.currDocId,
    currentDocumentInfo: state =>
      new ApiDocumentCard(JSON.parse(JSON.stringify(state.currDocInfo))),
    currentDocumentContinuity: state => state.currDocContinuity,
    currentDocumentAnnotation: state => state.currDocAnnotation,
    currentDocumentAttachment: state => state.currDocAttachment,
    currentDocumentChanges: state =>
      state.currDocChanges.map(i => JSON.parse(JSON.stringify(i))),
    currentDocumentClassifiers: state =>
      state.currDocClassifiers.map(i => JSON.parse(JSON.stringify(i))),

    currentDocumentScanContent: state =>
      state.currDocScanContent.map(i => JSON.parse(JSON.stringify(i))),
    currentDocumentScans: state =>
      state.currDocScanPages.map(i => JSON.parse(JSON.stringify(i))),

    currentDocumentTextContent: state =>
      state.currDocTextContent.map(i => JSON.parse(JSON.stringify(i))),
    currentDocumentTextHtml: state => state.currDocTextHtml,
    currentDocumentTextBlocks: state => state.currDocTextBlocks.map(i => i),
    currentDocumentHistory: state =>
      state.currDocHistory.map(i => JSON.parse(JSON.stringify(i))),
    currentDocumentDiscussions: state =>
      state.currDocDiscussions.map(i => JSON.parse(JSON.stringify(i))),
    currentDocumentRevisions: state => 
      state.currDocRevisions.map(i => JSON.parse(JSON.stringify(i))),
    currentDocumentAttachments: state => 
      state.currDocAttachments.map(i => JSON.parse(JSON.stringify(i))),

    documentIsLoading: state => Object.assign({}, state.loaderState),
    loadErrors: state => Object.assign({}, state.loadErrors),

    // -------------------------- SEARCH MUTATIONS --------------------------
    searchMatchesCount: state => state.searchMatchesCount,
    currentSearchMatch: state => state.currentSearchMatch,
    markedTextBlock: state => state.markedTextBlock,
    foundScanMatches: state => state.foundScanMatches.map( i => JSON.parse(JSON.stringify(i))),
  },

  mutations: {
    SET_CURRENT_DOCUMENT_ID(state, id) {
      state.currDocId = id;
    },
    RESET_CURRENT_DOCUMENT_DATA(state) {
      Object.keys(DOC_CONFIG).forEach(key => {
        DOC_CONFIG[key].properties.forEach(prop => {
          switch (prop.type) {
            case 'string':
              state[prop.property] = '';
              break;
            case 'object':
              state[prop.property] = {};
              break;
            case 'array':
              state[prop.property] = [];
              break;
          }
        });
      });
    },
    //  obj: {
    //    name:<string> - название свойства,
    //    isArray:<boolean> - будет записан 1 элемент в массив (не массив целиком),
    //    index:<number> - если isArray=true, то это индекс записываемого элемента,
    //    value: <any> - новое значение свойства
    //  }
    SET_CURRENT_DOCUMENT_PROPERTY_AS_NEW(state, obj) {
      if (obj.isArray) {
        Vue.set(state[obj.name], obj.index, obj.value);
      } else {
        if (typeof obj.value === 'object') {
          state[obj.name] = JSON.parse(JSON.stringify(obj.value));
        } else {
          state[obj.name] = obj.value;
        }
      }
    },
    //  obj: {
    //    name:<string> - название свойства,
    //    isArray:<boolean> - будет записан 1 элемент в массив (не массив целиком),
    //    index:<number> - если isArray=true, то это индекс записываемого элемента,
    //  }
    SET_CURRENT_DOCUMENT_PROPERTY_FROM_HISTORY(state, obj) {
      const valueInViewed = obj.isArray
        ? state.viewedDocs[state.currDocId][obj.name][obj.index]
        : state.viewedDocs[state.currDocId][obj.name];

      if (obj.isArray) {
        Vue.set(state[obj.name], obj.index, valueInViewed);
      } else {
        if (typeof valueInViewed === 'object') {
          state[obj.name] = JSON.parse(JSON.stringify(valueInViewed));
        } else {
          state[obj.name] = valueInViewed;
        }
      }
    },
    // obj is to the obj in SET_CURRENT_DOCUMENT_PROPERTY_AS_NEW
    SET_PROPERTY_IN_VIEWED(state, obj) {
      if (!state.viewedDocs[state.currDocId]) {
        state.viewedDocs[state.currDocId] = {};
      }
      if (obj.isArray) {
        if (!state.viewedDocs[state.currDocId][obj.name]) {
          state.viewedDocs[state.currDocId][obj.name] = [];
          Vue.set(
            state.viewedDocs[state.currDocId][obj.name],
            obj.index,
            obj.value
          );
        }
      } else {
        Vue.set(state.viewedDocs[state.currDocId], obj.name, obj.value);
      }
    },
    UPDATE_LOADER_STATE(state, obj) {
      state.loaderState[obj.name] = obj.status;
    },
    UPDATE_LOAD_ERROR(state, obj) {
      Vue.set(state.loadErrors[obj.name], 'status', obj.status);
      Vue.set(state.loadErrors[obj.name], 'message', obj.message);
    },
    UPDATE_INFO_PROPERTY(state, obj) {
      Vue.set(state.currDocInfo, obj.name, obj.value);
    },

    // -------------------------- SEARCH MUTATIONS --------------------------
    SET_SEARCH_MATCHES_COUNT(state, num) {
      state.searchMatchesCount = num;
    },
    SET_CURRENT_SEARCH_MATCH(state, num) {   
      if(num === 0) {
        state.currentSearchMatch = 0;
      }
      if(num === 1) {
        if(state.currentSearchMatch < state.searchMatchesCount) {
          state.currentSearchMatch += num;
        } else {
          state.currentSearchMatch = 1;
        }
      }
      if(num === -1) {
        if(state.currentSearchMatch > 1) { 
          state.currentSearchMatch += num;
        } else {
          state.currentSearchMatch = state.searchMatchesCount;
        }
      }
    },
    SET_MARKED_HTML(state, str) {
      state.markedTextBlock = str;
    },
    SET_FOUND_SCAN_MATCHES(state, arr) {
      arr.sort((a,b) => {
        let arrA = a.id.split('_')
        let arrB = b.id.split('_')
        let res = +arrA[0] - +arrB[0]

        if(!res) res = +arrA[1] - +arrB[1]

        return res
      })
      state.foundScanMatches = arr;
    }
  },

  actions: {
    updateCurrentDocumentInfoProperty({ commit }, obj) {
      commit('UPDATE_INFO_PROPERTY', obj);
    },
    async setCurrentDocumentId({ state, commit, dispatch }, id) {
      if (state.currDocId === id) return;  
      commit('SET_CURRENT_DOCUMENT_ID', id);
      commit('RESET_CURRENT_DOCUMENT_DATA');
      await dispatch('setCurrentDocumentProperty', { name: 'info' });
    },

    //  payload: {
    //    name:<string> - название свойства,
    // (?)param: <any> - параметры API запроса (при наличии)
    //  }
    async setCurrentDocumentProperty({ state, commit }, payload) {
      commit('UPDATE_LOAD_ERROR', {
        name: payload.name,
        status: false,
        message: ''
      });
      // При наличии свойств среди просмотренных документов - пропускаем
      let alreadyViewed = true;
      DOC_CONFIG[payload.name].properties.forEach(prop => {
        const isId = state.viewedDocs[state.currDocId];
        const isProp = isId && state.viewedDocs[state.currDocId][prop.property];

        if (!isProp) alreadyViewed = false;
      });
      if (alreadyViewed) {
        DOC_CONFIG[payload.name].properties.forEach(prop => {
          let dataObj = { name: prop.property };

          if (prop.isArray) {
            dataObj.isArray = true;
            dataObj.index = payload.param ? payload.param : 0;
          }
          commit('SET_CURRENT_DOCUMENT_PROPERTY_FROM_HISTORY', dataObj);
        });

        return;
      }

      try {
        commit('UPDATE_LOADER_STATE', {
          name: payload.name,
          status: true
        });
        // здесь происходит вызов метода, возвращающего информацию о документе 
        let resData = await apiDocuments[DOC_CONFIG[payload.name].apiMethod](
          state.currDocId,
          payload.param ? payload.param : ''
        );

        DOC_CONFIG[payload.name].properties.forEach(prop => {
          let modData =
            typeof resData === 'object'
              ? JSON.parse(JSON.stringify(resData))
              : resData;

          if (prop.modifyFunc) {
            modData = prop.modifyFunc(modData);
          }
          const dataObj = {
            name: prop.property,
            value: modData
          };

          if (prop.isArray) {
            dataObj.isArray = true;
            dataObj.index = payload.param ? payload.param : 0;
          }
          commit('SET_CURRENT_DOCUMENT_PROPERTY_AS_NEW', dataObj);
          commit('SET_PROPERTY_IN_VIEWED', dataObj);
        });
      } catch (err) {
        console.error(
          `Error on get document property ${payload.name} with: `,
          err.message ? err.message : err
        );
        commit('UPDATE_LOAD_ERROR', {
          name: payload.name,
          status: true,
          message: err.message ? err.message : err
        });
      } finally {
        commit('UPDATE_LOADER_STATE', {
          name: payload.name,
          status: false
        });
      }
    },

    // -------------------------- SEARCH ACTIONS --------------------------
    async getMatchesForHtml({ state, commit }, str){
      commit('UPDATE_LOADER_STATE', {
        name: 'search',
        status: true
      });
      try{
        let mode = 'alone' 

        if( str.charAt(0) === '*' ) mode = 'substr'
        if( str.charAt(0) === '"' && str.charAt(str.length - 1) === '"' ) mode = 'phrase'

        let matches 

        if( mode !== 'phrase' ){
          let res = await apiApp.getDataForTextSearch(str)
          
          matches = getMatches(res, state.currDocTextBlocks[0], mode);
        } else {
          let words = str.slice(1, -1).match(/[a-zа-яё]+/gi)
          let resArr = []

          for( let i=0; i < words.length; i++) {
            let wordRes =  await apiApp.getDataForTextSearch(words[i])

            resArr.push(wordRes) 
          }
          matches = getMatchesForPhrases(resArr, state.currDocTextBlocks[0]);
        }
        commit('SET_SEARCH_MATCHES_COUNT', matches.length);
        commit('SET_CURRENT_SEARCH_MATCH', 0)
         
        const openTag = (ind) => `<span id="found_match_${ind}" style="background-color:rgba(226,75,15,.3);">`;
        const closeTag = `</span>`
        const newHtml =  markTextWithMatches(matches, state.currDocTextBlocks[0], openTag, closeTag);
   
        commit('SET_MARKED_HTML', newHtml);
      } catch(err) {
        console.error('Error on search in document text with:', err.message? err.message: err);
      } finally {
        commit('UPDATE_LOADER_STATE', {
          name: 'search',
          status: false
        });
      }
    },

    async getMatchesForScan({ state, commit }, str){
      commit('UPDATE_LOADER_STATE', {
        name: 'search',
        status: true
      });
      try{
        const res = await apiDocuments.findByScan(state.currDocId, str)
        const sortedPagesNums = Object.keys(res).sort((a, b) => ((+a)-(+b))? 1: -1)
        let matches = []

        sortedPagesNums.forEach( item => {
          res[item].forEach((match, matchNum) => {
            matches.push({
              id: `${item}_${matchNum}`,
              page: item,
              coords: match
            })
          })
        })
        commit('SET_SEARCH_MATCHES_COUNT', matches.length);
        commit('SET_CURRENT_SEARCH_MATCH', 0);
        commit('SET_FOUND_SCAN_MATCHES', matches);
      } catch(err) {
        console.error('Error on search in document scan with:', err.message? err.message: err);
      } finally {
        commit('UPDATE_LOADER_STATE', {
          name: 'search',
          status: false
        });
      }
    },
    scrollToAnotherFoundMatch({state, commit}, direction) {
      switch(direction) {
        case 'next':
          commit('SET_CURRENT_SEARCH_MATCH', 1);
          break;
        case 'prev':
          commit('SET_CURRENT_SEARCH_MATCH', -1); 
          break;
        default:
          console.error('Wrong direction from search component');
      }
    },
    cleanSearchData({ commit }) {
      commit('SET_SEARCH_MATCHES_COUNT', null);
      commit('SET_CURRENT_SEARCH_MATCH', 0);
      commit('SET_MARKED_HTML', '');
      commit('SET_FOUND_SCAN_MATCHES', []);
    }
  }
};
